// # My Tasks Component
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import cacheManager from '../../../auth/cache_manager';
import { format, isToday, isThisWeek, isThisMonth, isBefore } from 'date-fns';
import { doc, getDoc, getFirestore, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import m1Access from '../m1_access';
import './mentee_tasks1.css';

// # Import icons from react-icons
import { 
  FaTasks, 
  FaCheck, 
  FaClock,
  FaExclamationCircle,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarAlt,
  FaHistory,
  FaListAlt,
  FaCheckCircle,
  FaHourglassHalf
} from 'react-icons/fa';

function MenteeTasks1({ isAdmin, isTutor, isMentorView, menteeId }) {
  const navigate = useNavigate();
  const { username } = useParams();
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [targetUserId, setTargetUserId] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  
  // # State for UI filters and tabs
  const [activeTab, setActiveTab] = useState('today');
  const [activeFilter, setActiveFilter] = useState('all');
  
  // # Create base path based on user role
  const basePath = m1Access.getBasePath({
    isAdmin,
    isTutor,
    isMentorView,
    username
  });

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Listen for theme changes from DashboardHeader
  useEffect(() => {
    const handleThemeChange = (event) => {
      setIsDarkMode(event.detail.isDark);
    };
    
    window.addEventListener('theme_change', handleThemeChange);
    
    return () => {
      window.removeEventListener('theme_change', handleThemeChange);
    };
  }, []);

  // # Get the correct user ID based on role context
  useEffect(() => {
    // # Use the access utility to determine the target user ID
    const userId = m1Access.getTargetUserId({
      isAdmin,
      isTutor,
      viewingMenteeId: menteeId || sessionStorage.getItem('currentViewingMenteeId'),
      currentUserId: user?.uid
    });
    
    setTargetUserId(userId);
    setIsMentor(userId !== user?.uid);
  }, [user?.uid, isAdmin, isTutor, menteeId]);

  // # Load tasks from Firestore
  useEffect(() => {
    const fetchTasks = async () => {
      if (!targetUserId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const db = getFirestore();
        const menteeRef = doc(db, 'db1_mentee', targetUserId);
        const menteeSnapshot = await getDoc(menteeRef);
        
        if (menteeSnapshot.exists()) {
          const menteeData = menteeSnapshot.data();
          const allTasks = [];
          
          // # Process all chapters and their tasks
          Object.entries(menteeData.chapters || {}).forEach(([chapterId, chapterData]) => {
            // # Get chapter metadata
            const chapterName = chapterData.metadata?.chapterName || chapterId;
            
            // # Process tasks in this chapter
            Object.entries(chapterData.tasks || {}).forEach(([taskId, task]) => {
              const taskDate = task.date ? new Date(task.date) : null;
              
              allTasks.push({
                id: taskId,
                ...task,
                chapter: chapterId,
                chapterName: chapterName,
                formattedDate: taskDate ? format(taskDate, 'MMM dd, yyyy') : 'No date',
                dateObj: taskDate,
                status: task.done ? 'completed' : 'pending'
              });
            });
          });
          
          setTasks(allTasks);
        } else {
          setTasks([]);
        }
      } catch (error) {
        console.error('Error loading tasks:', error);
        setError('Failed to load tasks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTasks();
  }, [targetUserId]);

  // # Filter tasks based on selected tab and filter
  const getFilteredTasks = () => {
    // # First, filter by time period (tab)
    let filteredByDate = [...tasks];
    
    if (activeTab === 'today') {
      filteredByDate = tasks.filter(task => 
        task.dateObj && isToday(task.dateObj)
      );
    } else if (activeTab === 'week') {
      filteredByDate = tasks.filter(task => 
        task.dateObj && isThisWeek(task.dateObj, { weekStartsOn: 1 })
      );
    } else if (activeTab === 'month') {
      filteredByDate = tasks.filter(task => 
        task.dateObj && isThisMonth(task.dateObj)
      );
    } else if (activeTab === 'passed') {
      filteredByDate = tasks.filter(task => 
        task.dateObj && isBefore(task.dateObj, new Date()) && !isToday(task.dateObj)
      );
    }
    // # 'all' tab will use the original tasks array, no date filtering needed
    
    // # Then, filter by completion status
    let filtered = filteredByDate;
    if (activeFilter === 'completed') {
      filtered = filteredByDate.filter(task => task.status === 'completed');
    } else if (activeFilter === 'pending') {
      filtered = filteredByDate.filter(task => task.status === 'pending');
    }
    
    // # Always sort by task name ascending
    return filtered.sort((a, b) => a.task_name.localeCompare(b.task_name));
  };

  // # Get task counts for filters and tabs
  const getCounts = () => {
    // # Count tasks for each time period
    const todayTasks = tasks.filter(task => task.dateObj && isToday(task.dateObj));
    const weekTasks = tasks.filter(task => task.dateObj && isThisWeek(task.dateObj, { weekStartsOn: 1 }));
    const monthTasks = tasks.filter(task => task.dateObj && isThisMonth(task.dateObj));
    const passedTasks = tasks.filter(task => task.dateObj && isBefore(task.dateObj, new Date()) && !isToday(task.dateObj));
    
    // # Get active tab's tasks
    let activeTasks;
    switch (activeTab) {
      case 'today':
        activeTasks = todayTasks;
        break;
      case 'week':
        activeTasks = weekTasks;
        break;
      case 'month':
        activeTasks = monthTasks;
        break;
      case 'passed':
        activeTasks = passedTasks;
        break;
      default:
        activeTasks = tasks;
    }
    
    // # Count by status within active tab
    const completedInActiveTab = activeTasks.filter(task => task.status === 'completed').length;
    const pendingInActiveTab = activeTasks.filter(task => task.status === 'pending').length;
    
    return {
      today: todayTasks.length,
      week: weekTasks.length,
      month: monthTasks.length,
      passed: passedTasks.length,
      all: activeTasks.length,
      completed: completedInActiveTab,
      pending: pendingInActiveTab
    };
  };

  const counts = getCounts();
  const filteredTasks = getFilteredTasks();

  // # Handle task completion toggle
  const handleTaskCompletion = async (taskId, chapterId, currentStatus) => {
    if (!targetUserId) return;

    try {
      const db = getFirestore();
      const menteeRef = doc(db, 'db1_mentee', targetUserId);
      
      // # Get current mentee data
      const menteeSnapshot = await getDoc(menteeRef);
      let menteeData = menteeSnapshot.exists() ? menteeSnapshot.data() : {};
      
      // # Update the task's done status
      if (menteeData.chapters?.[chapterId]?.tasks?.[taskId]) {
        menteeData.chapters[chapterId].tasks[taskId].done = !currentStatus;
        
        // # Save to Firestore
        await setDoc(menteeRef, menteeData);
        
        // # Update local state
        setTasks(prevTasks => 
          prevTasks.map(task => 
            task.id === taskId 
              ? { ...task, done: !currentStatus, status: !currentStatus ? 'completed' : 'pending' }
              : task
          )
        );
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  return (
    <div className={`ment_tsk1_container ${isDarkMode ? 'ment_tsk1_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={(newMode) => setIsDarkMode(newMode)} />
      
      <main className="ment_tsk1_content">
        <div className="ment_tsk1_title_box">
          <h1 className="ment_tsk1_title">
            <FaTasks className="ment_tsk1_title_icon" />
            My Tasks
            {isMentor && <span className="ment_tsk1_mentor_pill">
              {isAdmin ? 'Mentor' : 'Tutor'}
            </span>}
          </h1>
          
          {/* Add mentee info when viewing as mentor/tutor */}
          {isMentor && username && (
            <div className="ment_tsk1_mentee_info">
              Viewing tasks for: @{username}
            </div>
          )}
          
          <div className="ment_tsk1_summary_cards">
            <div className="ment_tsk1_summary_card total">
              <div className="ment_tsk1_summary_icon">
                <FaListAlt />
              </div>
              <div className="ment_tsk1_summary_content">
                <span className="ment_tsk1_summary_value">{tasks.length}</span>
                <span className="ment_tsk1_summary_label">Total Tasks</span>
              </div>
            </div>
            
            <div className="ment_tsk1_summary_card completed">
              <div className="ment_tsk1_summary_icon">
                <FaCheckCircle />
              </div>
              <div className="ment_tsk1_summary_content">
                <span className="ment_tsk1_summary_value">
                  {tasks.filter(task => task.status === 'completed').length}
                </span>
                <span className="ment_tsk1_summary_label">Completed</span>
              </div>
            </div>
            
            <div className="ment_tsk1_summary_card pending">
              <div className="ment_tsk1_summary_icon">
                <FaHourglassHalf />
              </div>
              <div className="ment_tsk1_summary_content">
                <span className="ment_tsk1_summary_value">
                  {tasks.filter(task => task.status === 'pending').length}
                </span>
                <span className="ment_tsk1_summary_label">Pending</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="ment_tsk1_tabs_container">
          <div className="ment_tsk1_tabs">
            <button 
              className={`ment_tsk1_tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              <FaListAlt className="ment_tsk1_tab_icon" />
              All
              <span className="ment_tsk1_count">{tasks.length}</span>
            </button>
            
            <button 
              className={`ment_tsk1_tab ${activeTab === 'today' ? 'active' : ''}`}
              onClick={() => setActiveTab('today')}
            >
              <FaCalendarDay className="ment_tsk1_tab_icon" />
              Today
              <span className="ment_tsk1_count">{counts.today}</span>
            </button>
            
            <button 
              className={`ment_tsk1_tab ${activeTab === 'week' ? 'active' : ''}`}
              onClick={() => setActiveTab('week')}
            >
              <FaCalendarWeek className="ment_tsk1_tab_icon" />
              This Week
              <span className="ment_tsk1_count">{counts.week}</span>
            </button>
            
            <button 
              className={`ment_tsk1_tab ${activeTab === 'month' ? 'active' : ''}`}
              onClick={() => setActiveTab('month')}
            >
              <FaCalendarAlt className="ment_tsk1_tab_icon" />
              This Month
              <span className="ment_tsk1_count">{counts.month}</span>
            </button>
            
            <button 
              className={`ment_tsk1_tab ${activeTab === 'passed' ? 'active' : ''}`}
              onClick={() => setActiveTab('passed')}
            >
              <FaHistory className="ment_tsk1_tab_icon" />
              Passed
              <span className="ment_tsk1_count">{counts.passed}</span>
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="ment_tsk1_filters_container">
          <div className="ment_tsk1_filters">
            <button 
              className={`ment_tsk1_filter ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All
              <span className="ment_tsk1_filter_count">{counts.all}</span>
            </button>
            
            <button 
              className={`ment_tsk1_filter ${activeFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setActiveFilter('completed')}
            >
              Completed
              <span className="ment_tsk1_filter_count">{counts.completed}</span>
            </button>
            
            <button 
              className={`ment_tsk1_filter ${activeFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setActiveFilter('pending')}
            >
              Pending
              <span className="ment_tsk1_filter_count">{counts.pending}</span>
            </button>
          </div>
        </div>
        
        {/* Task Cards Grid */}
        <div className="ment_tsk1_tasks_content">
          {loading ? (
            <div className="ment_tsk1_loading">
              <div className="ment_tsk1_loading_spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : error ? (
            <div className="ment_tsk1_error">
              <FaExclamationCircle className="ment_tsk1_error_icon" />
              <p>{error}</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="ment_tsk1_empty">
              <p>No tasks found for this period.</p>
            </div>
          ) : (
            <div className="ment_tsk1_grid">
              {filteredTasks.map(task => (
                <div 
                  key={task.id} 
                  className={`ment_tsk1_card source-${task.source?.toLowerCase()} ${task.status}`}
                >
                  <div className="ment_tsk1_card_header">
                    <div className="ment_tsk1_status">
                      <div 
                        className={`ment_tsk1_checkbox ${task.status}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTaskCompletion(task.id, task.chapter, task.done);
                        }}
                      >
                        {task.status === 'completed' ? (
                          <FaCheck className="ment_tsk1_check_icon" />
                        ) : (
                          <div className="ment_tsk1_checkbox_empty" />
                        )}
                      </div>
                    </div>
                    <h3 className="ment_tsk1_card_title">{task.task_name}</h3>
                  </div>
                  
                  <div className="ment_tsk1_card_content">
                    <div className="ment_tsk1_card_detail">
                      <span className="ment_tsk1_label">Chapter:</span>
                      <span className="ment_tsk1_value">{task.chapterName}</span>
                    </div>
                    
                    <div className="ment_tsk1_card_detail">
                      <span className="ment_tsk1_label">Source:</span>
                      <span className="ment_tsk1_value source">{task.source}</span>
                    </div>
                    
                    <div className="ment_tsk1_card_detail">
                      <span className="ment_tsk1_label">Type:</span>
                      <span className="ment_tsk1_value type">{task.type}</span>
                    </div>
                    
                    <div className="ment_tsk1_card_detail">
                      <span className="ment_tsk1_label">Date:</span>
                      <span className="ment_tsk1_value date">{task.formattedDate}</span>
                    </div>
                    
                    <div className="ment_tsk1_card_detail">
                      <span className="ment_tsk1_label">Duration:</span>
                      <span className="ment_tsk1_value duration">{task.duration} min</span>
                    </div>
                  </div>
                  
                  <div className="ment_tsk1_card_footer">
                    <span className="ment_tsk1_plan">{task.plan}</span>
                    <span className="ment_tsk1_day">Day {task.day}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default MenteeTasks1;
